// Answer Format Helper for HeartSync
// This file provides answer options based on question format

export const ANSWER_FORMATS = {
  // Default agreement scale
  agreement: {
    1: 'Strongly Disagree',
    2: 'Disagree',
    3: 'Neutral',
    4: 'Agree',
    5: 'Strongly Agree'
  },
  
  // Frequency scale
  frequency: {
    1: 'Never',
    2: 'Rarely',
    3: 'Sometimes',
    4: 'Often',
    5: 'Always'
  },
  
  // Reversed frequency (for negative questions where "Never" is good)
  frequency_reversed: {
    1: 'Always',
    2: 'Often',
    3: 'Sometimes',
    4: 'Rarely',
    5: 'Never'
  },
  
  // Comfort scale
  comfort: {
    1: 'Very Uncomfortable',
    2: 'Uncomfortable',
    3: 'Neutral',
    4: 'Comfortable',
    5: 'Very Comfortable'
  },
  
  // Quality scale
  quality: {
    1: 'Very Poor',
    2: 'Poor',
    3: 'Fair',
    4: 'Good',
    5: 'Excellent'
  },
  
  // Satisfaction scale
  satisfaction: {
    1: 'Very Dissatisfied',
    2: 'Dissatisfied',
    3: 'Neutral',
    4: 'Satisfied',
    5: 'Very Satisfied'
  },
  
  // Confidence scale
  confidence: {
    1: 'Not at all Confident',
    2: 'Slightly Confident',
    3: 'Moderately Confident',
    4: 'Very Confident',
    5: 'Extremely Confident'
  },
  
  // Ease scale
  ease: {
    1: 'Very Difficult',
    2: 'Difficult',
    3: 'Moderate',
    4: 'Easy',
    5: 'Very Easy'
  },
  
  // Importance scale
  importance: {
    1: 'Not Important',
    2: 'Slightly Important',
    3: 'Moderately Important',
    4: 'Very Important',
    5: 'Extremely Important'
  },
  
  // Likelihood scale
  likelihood: {
    1: 'Very Unlikely',
    2: 'Unlikely',
    3: 'Neutral',
    4: 'Likely',
    5: 'Very Likely'
  }
};

/**
 * Get answer options for a question
 * @param {Object} question - The question object from factorInterpretations
 * @returns {Array} Array of {value, text} objects for answer options
 */
export const getAnswerOptions = (question) => {
  // If question has custom options defined, use those
  if (question.options && Array.isArray(question.options)) {
    return question.options;
  }
  
  // Get the format type (default to 'agreement')
  const formatType = question.answerFormat || 'agreement';
  
  // Get the format definition
  const format = ANSWER_FORMATS[formatType] || ANSWER_FORMATS.agreement;
  
  // Convert to array of {value, text} objects
  return Object.entries(format).map(([value, text]) => ({
    value: parseInt(value),
    text: text
  }));
};

/**
 * Get the label for a specific answer value
 * @param {number} value - The answer value (1-5)
 * @param {Object} question - The question object
 * @returns {string} The text label for the answer
 */
export const getAnswerLabel = (value, question) => {
  if (!value) return 'Not Answered';
  
  // If question has custom options, find the matching one
  if (question.options && Array.isArray(question.options)) {
    const option = question.options.find(opt => opt.value === value);
    return option ? option.text : `Option ${value}`;
  }
  
  // Get the format type
  const formatType = question.answerFormat || 'agreement';
  const format = ANSWER_FORMATS[formatType] || ANSWER_FORMATS.agreement;
  
  return format[value] || `${value}`;
};

/**
 * Get color class based on answer value (for visual feedback)
 * @param {number} value - The answer value (1-5)
 * @returns {string} Tailwind color classes
 */
export const getAnswerColor = (value) => {
  switch (value) {
    case 1:
      return 'bg-red-100 text-red-800 border-red-300';
    case 2:
      return 'bg-orange-100 text-orange-800 border-orange-300';
    case 3:
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 4:
      return 'bg-lime-100 text-lime-800 border-lime-300';
    case 5:
      return 'bg-green-100 text-green-800 border-green-300';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-300';
  }
};

export default {
  ANSWER_FORMATS,
  getAnswerOptions,
  getAnswerLabel,
  getAnswerColor
};
